import { SubscriptionLimit } from '../components/Subscriptions/types';
import { PermissionDisplayMetadata } from '../services/permissionService';
import type { Feature } from '../contexts/PermissionContext';
import { TIER_AUDIO_LIMITS } from '../config/audioLimitsConfig';

export interface TierFeature {
  text: string;
  included: boolean;
  bold?: boolean;
  isHeader?: boolean; // True for "Everything in X plus:" lines - no checkmark
}

type GetPermissionMetadataFn = (feature: Feature) => PermissionDisplayMetadata | undefined;

interface TierFeatureOptions {
  tier: string;
  limits: SubscriptionLimit | null | undefined;
  getPermissionMetadata: GetPermissionMetadataFn;
  freeLimits?: SubscriptionLimit | null;
  starterLimits?: SubscriptionLimit | null;
}

/**
 * Determines which previous tier to reference based on feature overlap.
 * If Pro has all Starter features, reference Starter. Otherwise reference Free.
 */
function determinePreviousTier(
  currentTier: string,
  currentLimits: SubscriptionLimit | null | undefined,
  freeLimits: SubscriptionLimit | null | undefined,
  starterLimits: SubscriptionLimit | null | undefined
): 'free' | 'starter' | null {
  if (currentTier === 'free') {
    return null; // Free tier has no previous tier
  }

  if (currentTier === 'starter') {
    return 'free';
  }

  if (currentTier === 'pro') {
    // Check if Pro has all Starter features
    if (starterLimits && currentLimits) {
      const starterFeatures = starterLimits.features;
      const proFeatures = currentLimits.features;
      
      // Check if Pro has all Starter features
      const hasAllStarterFeatures = 
        (starterFeatures.crop_image === false || proFeatures.crop_image === true) &&
        (starterFeatures.instant_uploads === false || proFeatures.instant_uploads === true) &&
        (starterFeatures.scheduled_uploads === false || proFeatures.scheduled_uploads === true);
      
      // Also check limits
      const hasStarterLimits = 
        (starterLimits.daily_instant_upload_limit === null || 
         currentLimits.daily_instant_upload_limit === null ||
         currentLimits.daily_instant_upload_limit >= (starterLimits.daily_instant_upload_limit || 0)) &&
        (starterLimits.allow_scheduled_uploads === false || currentLimits.allow_scheduled_uploads === true);
      
      if (hasAllStarterFeatures && hasStarterLimits) {
        return 'starter';
      }
    }
    return 'free';
  }

  return 'free'; // Default fallback
}

/**
 * Gets features unique to the current tier (not in previous tier)
 */
function getUniqueFeatures(
  currentTier: string,
  currentLimits: SubscriptionLimit | null | undefined,
  previousTier: 'free' | 'starter' | null,
  previousLimits: SubscriptionLimit | null | undefined
): Set<string> {
  if (!currentLimits || !previousLimits || previousTier === null) {
    return new Set();
  }

  const uniqueFeatures = new Set<string>();

  // Check feature flags
  const currentFeatures = currentLimits.features;
  const previousFeatures = previousLimits.features;

  if (currentFeatures.crop_image && !previousFeatures.crop_image) {
    uniqueFeatures.add('crop_image');
  }
  if (currentFeatures.auto_resize_image && !previousFeatures.auto_resize_image) {
    uniqueFeatures.add('auto_resize_image');
  }
  if (currentFeatures.upload_defaults && !previousFeatures.upload_defaults) {
    uniqueFeatures.add('upload_defaults');
  }
  if (currentFeatures.bulk_upload && !previousFeatures.bulk_upload) {
    uniqueFeatures.add('bulk_upload');
  }

  // Check limits
  if (currentLimits.allow_scheduled_uploads && !previousLimits.allow_scheduled_uploads) {
    uniqueFeatures.add('scheduled_uploads');
  }

  return uniqueFeatures;
}

/**
 * Generates standardized feature list for a subscription tier.
 * Uses "Everything in X plus:" format to avoid repetition.
 */
export function getTierFeatures(options: TierFeatureOptions): TierFeature[] {
  const { tier, limits, getPermissionMetadata, freeLimits, starterLimits } = options;
  const features: TierFeature[] = [];

  if (tier === 'free') {
    // Free tier: Just show what's included (don't show features that are not available)
    if (limits) {
      const instantLimit = limits.daily_instant_upload_limit;
      const instantMeta = getPermissionMetadata('instant_uploads' as Feature);
      const instantDisplayName = instantMeta?.display_name?.toLowerCase() || 'instant uploads';

      features.push({
        text: instantLimit === null
          ? `Unlimited ${instantDisplayName}`
          : `${instantLimit} ${instantDisplayName} per day`,
        included: true
      });

      // Audio limits for free tier
      const audioLimits = TIER_AUDIO_LIMITS.free;
      features.push({
        text: `Up to ${audioLimits.maxFileSizeMb}MB audio file`,
        included: true
      });
      features.push({
        text: `Supported audio formats ${audioLimits.displayFormats}`,
        included: true
      });

      // Auto Resize Image (available in Free)
      if (limits.features.auto_resize_image) {
        const autoResizeMeta = getPermissionMetadata('auto_resize_image' as Feature);
        features.push({
          text: autoResizeMeta?.display_name || 'Auto Resize Image',
          included: true
        });
      }
    } else {
      // Fallback if limits not available
      features.push({
        text: '1 instant upload per day',
        included: true
      });
      features.push({
        text: 'Up to 10MB audio file',
        included: true
      });
      features.push({
        text: 'Supported audio formats MP3',
        included: true
      });
      features.push({
        text: 'Auto Resize Image',
        included: true
      });
    }
  } else if (tier === 'starter') {
    // Starter tier: "Everything in Free, plus:"
    features.push({
      text: 'Everything in Free, plus:',
      included: true,
      bold: true,
      isHeader: true // No checkmark
    });

    if (limits) {
      // Audio limits for starter tier
      const audioLimits = TIER_AUDIO_LIMITS.starter;
      features.push({
        text: `Up to ${audioLimits.maxFileSizeMb}MB audio file`,
        included: true
      });
      features.push({
        text: `Supported audio formats MP3, M4A, AAC, OGG`,
        included: true
      });

      // 3 scheduled uploads per day
      const scheduledLimit = limits.daily_scheduled_upload_limit;
      const scheduledMeta = getPermissionMetadata('scheduled_uploads' as Feature);
      const scheduledDisplayName = scheduledMeta?.display_name?.toLowerCase() || 'scheduled uploads';

      if (limits.allow_scheduled_uploads) {
        // Add "(up to 30 days in advance)" suffix for scheduled uploads
        const scheduledSuffix = ' (up to 30 days in advance)';
        features.push({
          text: scheduledLimit === null
            ? `Unlimited ${scheduledDisplayName}${scheduledSuffix}`
            : scheduledLimit === 0
            ? `No ${scheduledDisplayName}`
            : `${scheduledLimit} ${scheduledDisplayName} per day${scheduledSuffix}`,
          included: true
        });
      }

      // Auto Resize Image — only list here if Free doesn't already have it
      if (limits.features.auto_resize_image && !freeLimits?.features?.auto_resize_image) {
        const autoResizeMeta = getPermissionMetadata('auto_resize_image' as Feature);
        features.push({
          text: autoResizeMeta?.display_name || 'Auto Resize Image',
          included: true
        });
      }

      // Crop Image (now available in Starter)
      if (limits.features.crop_image) {
        const cropImageMeta = getPermissionMetadata('crop_image' as Feature);
        features.push({
          text: cropImageMeta?.display_name || 'Crop Image',
          included: true
        });
      }

      // Optional Watermark (now available in Starter)
      if (limits.features.watermark_optional) {
        const watermarkMeta = getPermissionMetadata('watermark_optional' as Feature);
        features.push({
          text: watermarkMeta?.display_name || 'Optional Watermark',
          included: true
        });
      }
    } else {
      // Fallback if limits not available
      const instantMeta = getPermissionMetadata('instant_uploads' as Feature);
      const scheduledMeta = getPermissionMetadata('scheduled_uploads' as Feature);
      const cropImageMeta = getPermissionMetadata('crop_image' as Feature);
      const watermarkMeta = getPermissionMetadata('watermark_optional' as Feature);

      features.push({
        text: `Unlimited ${instantMeta?.display_name?.toLowerCase() || 'instant uploads'}`,
        included: true
      });
      features.push({
        text: 'Up to 25MB audio file',
        included: true
      });
      features.push({
        text: 'Supported audio formats MP3, M4A, AAC, OGG',
        included: true
      });
      features.push({
        text: `3 ${scheduledMeta?.display_name?.toLowerCase() || 'scheduled uploads'} per day (up to 30 days in advance)`,
        included: true
      });
      features.push({
        text: cropImageMeta?.display_name || 'Crop Image',
        included: true
      });
      features.push({
        text: watermarkMeta?.display_name || 'Optional Watermark',
        included: true
      });
    }
  } else if (tier === 'pro') {
    // Pro tier: Determine if we should reference Free or Starter
    const previousTier = determinePreviousTier(tier, limits, freeLimits, starterLimits);

    if (previousTier === 'starter') {
      features.push({
        text: 'Everything in Starter, plus:',
        included: true,
        bold: true,
        isHeader: true // No checkmark
      });
    } else {
      features.push({
        text: 'Everything in Free, plus:',
        included: true,
        bold: true,
        isHeader: true // No checkmark
      });
    }

    if (limits) {
      // Get unique features for Pro (not in previous tier)
      const uniqueFeatures = getUniqueFeatures(tier, limits, previousTier, previousTier === 'starter' ? starterLimits : freeLimits);

      // Audio limits for pro tier
      const audioLimits = TIER_AUDIO_LIMITS.pro;
      features.push({
        text: `Up to ${audioLimits.maxFileSizeMb}MB audio file`,
        included: true
      });
      features.push({
        text: `Supported audio formats WAV, FLAC, MP3, AIFF, M4A, AAC, OGG`,
        included: true
      });

      // Unlimited scheduled uploads (if not already in previous tier)
      if (limits.allow_scheduled_uploads) {
        const scheduledLimit = limits.daily_scheduled_upload_limit;
        const scheduledMeta = getPermissionMetadata('scheduled_uploads' as Feature);
        const scheduledDisplayName = scheduledMeta?.display_name?.toLowerCase() || 'scheduled uploads';
        const scheduledSuffix = ' (up to 30 days in advance)';

        // Only show if it's different from previous tier
        if (uniqueFeatures.has('scheduled_uploads') || scheduledLimit === null) {
          features.push({
            text: scheduledLimit === null
              ? `Unlimited ${scheduledDisplayName}${scheduledSuffix}`
              : `${scheduledLimit} ${scheduledDisplayName} per day${scheduledSuffix}`,
            included: true
          });
        }
      }

      // Crop Image — only show if Starter doesn't already have it
      if (limits.features.crop_image && !starterLimits?.features?.crop_image) {
        const cropImageMeta = getPermissionMetadata('crop_image' as Feature);
        features.push({
          text: cropImageMeta?.display_name || 'Crop Image',
          included: true
        });
      }

      // Upload Defaults
      if (limits.allow_upload_defaults) {
        const uploadDefaultsMeta = getPermissionMetadata('upload_defaults' as Feature);
        features.push({
          text: uploadDefaultsMeta?.display_name || 'Upload Defaults',
          included: true
        });
      }

      // Bulk Upload
      if (limits.features.bulk_upload) {
        const bulkUploadMeta = getPermissionMetadata('bulk_upload' as Feature);
        features.push({
          text: bulkUploadMeta?.display_name || 'Bulk Upload',
          included: true
        });
      }

      // Leaderboard
      if (limits.features.leaderboard) {
        const leaderboardMeta = getPermissionMetadata('leaderboard' as Feature);
        features.push({
          text: leaderboardMeta?.display_name || 'Leaderboard',
          included: true
        });
      }
    } else {
      // Fallback if limits not available
      const scheduledMeta = getPermissionMetadata('scheduled_uploads' as Feature);
      const uploadDefaultsMeta = getPermissionMetadata('upload_defaults' as Feature);
      const bulkUploadMeta = getPermissionMetadata('bulk_upload');
      const leaderboardMeta = getPermissionMetadata('leaderboard');

      features.push({
        text: 'Up to 75MB audio file',
        included: true
      });
      features.push({
        text: 'Supported audio formats WAV, FLAC, MP3, AIFF, M4A, AAC, OGG',
        included: true
      });
      features.push({
        text: `Unlimited ${scheduledMeta?.display_name?.toLowerCase() || 'scheduled uploads'} (up to 30 days in advance)`,
        included: true
      });
      features.push({
        text: uploadDefaultsMeta?.display_name || 'Upload Defaults',
        included: true
      });
      features.push({
        text: bulkUploadMeta?.display_name || 'Bulk Upload',
        included: true
      });
      features.push({
        text: leaderboardMeta?.display_name || 'Leaderboard',
        included: true
      });
    }
  } else if (tier === 'family') {
    // Family tier: Everything in Pro, plus family features
    features.push({
      text: 'Everything in Pro, plus:',
      included: true,
      bold: true,
      isHeader: true // No checkmark
    });

    features.push({
      text: 'Add up to 3 family members',
      included: true
    });

    features.push({
      text: 'Family members get Pro-level features',
      included: true
    });

    features.push({
      text: 'Shared subscription management',
      included: true
    });
  } else {
    // Other tiers (fallback)
    features.push({
      text: 'Everything in Free, plus:',
      included: true,
      bold: true,
      isHeader: true
    });

    if (limits) {
      const instantLimit = limits.daily_instant_upload_limit;
      const instantMeta = getPermissionMetadata('instant_uploads' as Feature);
      const instantDisplayName = instantMeta?.display_name?.toLowerCase() || 'instant uploads';
      
      features.push({
        text: instantLimit === null
          ? `Unlimited ${instantDisplayName}`
          : `${instantLimit} ${instantDisplayName} per day`,
        included: true
      });

      if (limits.allow_scheduled_uploads) {
        const scheduledLimit = limits.daily_scheduled_upload_limit;
        const scheduledMeta = getPermissionMetadata('scheduled_uploads' as Feature);
        const scheduledDisplayName = scheduledMeta?.display_name?.toLowerCase() || 'scheduled uploads';
        const scheduledSuffix = ' (up to 30 days in advance)';

        features.push({
          text: scheduledLimit === null
            ? `Unlimited ${scheduledDisplayName}${scheduledSuffix}`
            : scheduledLimit === 0
            ? `No ${scheduledDisplayName}`
            : `${scheduledLimit} ${scheduledDisplayName} per day${scheduledSuffix}`,
          included: true
        });
      }

      if (limits.allow_upload_defaults) {
        const uploadDefaultsMeta = getPermissionMetadata('upload_defaults' as Feature);
        features.push({
          text: uploadDefaultsMeta?.display_name || 'Upload Defaults',
          included: true
        });
      }
    }
  }

  return features;
}










